/** What the attention strip lists: the things that go wrong without anyone
 *  looking. Built from the desk's health report, the campaign drafts that
 *  failed to publish, the Threads tokens near their expiry and the posts a
 *  publish has been stuck on. */

import type { DeskErrorGroup, DeskHealth, Draft, ScheduleItem, Target } from '@/api/client'

export type AttentionTone = 'failed' | 'publishing'

export interface AttentionDetail {
  /** Where it happened: the app and channel, the job's channel. */
  where: string
  /** What it was: a post title, a count. */
  what?: string
  /** The platform's or the job's own words, whole. */
  message?: string
  at?: string
}

export interface AttentionItem {
  key: string
  tone: AttentionTone
  label: string
  count: number
  to: string
  details: AttentionDetail[]
}

/** A draft as the failed list returns it (the reason and update time included). */
export type DraftWithError = Draft

/** The refresher renews a Threads token from 14 days out; one still inside a
 *  week of lapsing did not renew (internal/target/refresh.go). */
export const TOKEN_WARN_DAYS = 7
/** A publish under way longer than this has likely died with the row left
 *  "publishing". */
export const STUCK_PUBLISH_MINUTES = 10

export interface AttentionExtra {
  targets?: Target[] | null
  /** Items the schedule feed has in "publishing". */
  publishing?: ScheduleItem[] | null
  now?: Date
}

const DAY_MS = 86_400_000

function where(it: ScheduleItem): string {
  const owner = it.app?.name ?? it.campaign?.name ?? ''
  const ch = it.platform === 'reddit' && it.channel ? `r/${it.channel}` : it.target?.name ? `@${it.target.name}` : it.platform
  return [owner, ch].filter(Boolean).join(' ')
}

const JOB_LABEL: Record<string, string> = {
  redeem_publish: '데스크 글 게시',
  redeem_watch: '받은편지함 확인',
  redeem_scan: '댓글 스캔',
}

const CHANNEL: Record<string, string> = { reddit: 'Reddit', bluesky: 'Bluesky' }

function groupDetail(g: DeskErrorGroup, fallback: string): AttentionDetail {
  return {
    where: g.channel ? (CHANNEL[g.channel] ?? g.channel) : fallback,
    what: `${g.count}회`,
    message: g.message,
    at: g.last_at,
  }
}

export function attentionItems(
  health?: DeskHealth | null,
  failedDrafts?: DraftWithError[] | null,
  extra: AttentionExtra = {},
): AttentionItem[] {
  const items: AttentionItem[] = []
  const now = extra.now ?? new Date()

  for (const j of health?.jobs ?? []) {
    if (!j.unhealthy) continue
    items.push({
      key: `job:${j.job}`,
      tone: 'failed',
      label: `${JOB_LABEL[j.job] ?? j.job} 연속 실패`,
      count: j.consecutive_errors,
      to: `/jobs?job=${encodeURIComponent(j.job)}`,
      details: j.errors.map((g) => groupDetail(g, j.job === 'redeem_publish' ? '게시' : '데스크')),
    })
  }

  const posts = health?.failed_posts ?? []
  if (posts.length > 0) {
    items.push({
      key: 'desk-posts',
      tone: 'failed',
      label: '데스크 글 게시 실패',
      count: posts.length,
      to: '/schedule?status=failed&source=redeem',
      details: posts.map((p) => ({
        where: [p.app_name, p.platform === 'reddit' && p.channel ? `r/${p.channel}` : (CHANNEL[p.platform] ?? p.platform)]
          .filter(Boolean)
          .join(' '),
        what: p.title,
        message: p.error_msg || '사유가 기록되지 않았다',
        at: p.updated_at,
      })),
    })
  }

  const drafts = failedDrafts ?? []
  if (drafts.length > 0) {
    items.push({
      key: 'campaign-drafts',
      tone: 'failed',
      label: '캠페인 글 게시 실패',
      count: drafts.length,
      to: '/schedule?status=failed&source=campaign',
      details: drafts.map((d) => ({
        where: d.CampaignName || d.Platform,
        what: d.BodyText.split('\n')[0].slice(0, 60),
        message: d.RejectionReason || '사유가 기록되지 않았다',
        at: d.UpdatedAt ?? d.ScheduledAt ?? undefined,
      })),
    })
  }

  const stuck = (extra.publishing ?? []).filter((it) => {
    const t = new Date(it.updated_at).getTime()
    return it.status === 'publishing' && !Number.isNaN(t) && now.getTime() - t > STUCK_PUBLISH_MINUTES * 60_000
  })
  if (stuck.length > 0) {
    items.push({
      key: 'stuck-publishing',
      tone: 'publishing',
      label: `${STUCK_PUBLISH_MINUTES}분 넘게 게시 중`,
      count: stuck.length,
      to: '/schedule?status=publishing',
      details: stuck.map((it) => ({
        where: where(it),
        what: it.title || it.excerpt.split('\n')[0].slice(0, 60),
        message: '게시 도중 멈췄을 수 있다. 플랫폼에 글이 올라갔는지 보고, 상세에서 처리한다.',
        at: it.updated_at,
      })),
    })
  }

  const expiring = (extra.targets ?? [])
    .filter((t) => t.status === 'active' && t.token_expires_at)
    .map((t) => ({ t, days: Math.floor((new Date(t.token_expires_at!).getTime() - now.getTime()) / DAY_MS) }))
    .filter((x) => !Number.isNaN(x.days) && x.days < TOKEN_WARN_DAYS)
  if (expiring.length > 0) {
    items.push({
      key: 'token-expiry',
      tone: expiring.some((x) => x.days < 2) ? 'failed' : 'publishing',
      label: 'Threads 토큰 만료 임박',
      count: expiring.length,
      to: '/targets',
      details: expiring.map(({ t, days }) => ({
        where: `@${t.name}`,
        what: days < 0 ? '만료됨' : days === 0 ? '오늘 만료' : `D-${days}`,
        message: '만료 14일 전부터 자동으로 갱신하는데 아직 그대로다. 작업 기록의 threads_token_refresh를 보고, 안 되면 계정을 다시 연결한다.',
        at: t.token_expires_at,
      })),
    })
  }

  const failedReq = health?.failed_requests
  if (failedReq && failedReq.count > 0) {
    items.push({
      key: 'failed-requests',
      tone: 'failed',
      label: '코드 전송 실패',
      count: failedReq.count,
      to: '/redeem/requests?state=failed',
      details: failedReq.groups.map((g) => groupDetail(g, '데스크')),
    })
  }

  const stuckReq = health?.stuck_requests
  if (stuckReq && stuckReq.count > 0) {
    items.push({
      key: 'stuck-requests',
      tone: 'publishing',
      label: `${stuckReq.threshold_minutes}분 넘게 멈춘 요청`,
      count: stuckReq.count,
      to: '/redeem/requests?state=new',
      details: Object.entries(stuckReq.by_state).map(([state, n]) => ({
        where: state,
        what: `${n}건`,
        at: stuckReq.oldest_at,
      })),
    })
  }

  if (health?.watch_paused) {
    items.push({
      key: 'watch-paused',
      tone: 'publishing',
      label: 'Reddit 댓글 감시 일시정지',
      count: 0,
      to: '/redeem/requests',
      details: [{ where: 'Reddit', what: '새 요청을 읽지 않는다' }],
    })
  }

  return items
}
